import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Check, AlertTriangle, RefreshCw, FileText, Trash2, ScanLine } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DDTCameraScanner — Magazzino carica il PDF del DDT e l'AI estrae
 * codici, pesi, prezzi e sconti % da applicare all'ordine.
 *
 * Props:
 *   open        bool — visibilità dialog
 *   onClose     fn() — chiude il dialog
 *   orderId     string — id dell'ordine da aggiornare
 *   orderItems  array — items dell'ordine (per UI di match/sostituzione)
 *   onApply     fn(payload) — righe da applicare al form ordine
 *   onAttached  fn(attachment) — chiamato quando il PDF viene allegato all'ordine
 */
const DDTCameraScanner = ({ open, onClose, orderId, orderItems = [], onApply, onAttached }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [result, setResult] = useState(null);
  const [decisions, setDecisions] = useState({});
  // Sostituzione completa: se ON, l'utente sostituisce tutte le righe dell'ordine con quelle del DDT
  const [replaceMode, setReplaceMode] = useState(false);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!(f.type === "application/pdf" || /\.pdf$/i.test(f.name || ""))) {
      toast.error("Formato non valido: carica il PDF del DDT");
      e.target.value = "";
      return;
    }
    setFile(f);
    setResult(null);
    setDecisions({});
    e.target.value = "";
  };

  const removeFile = () => {
    setFile(null);
    setResult(null);
    setDecisions({});
  };

  const sendToServer = async () => {
    if (!file || !orderId) return;
    setScanning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "ddt.pdf");
      const r = await axios.post(`${API}/orders/${orderId}/scan-ddt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const data = r.data;
      if (data?.error) {
        toast.error(data.error);
        setResult(data);
        return;
      }
      const init = {};
      (data.matched || []).forEach((m) => {
        init[m.ddt_index] = {
          order_item_index: m.order_item_index,  // may be null
          action: m.action,
          quantity: m.scanned_quantity || 0,
          unit: m.scanned_unit || "Kg",
          code: m.scanned_code || "",
          description: m.scanned_description || "",
          price: m.scanned_price ?? "",
          discount_percent: m.scanned_discount_percent ?? 0,
        };
      });
      setDecisions(init);
      setResult(data);
      const auto = (data.matched || []).filter((m) => m.action === "auto").length;
      const sub = (data.matched || []).filter((m) => m.action === "substitute").length;
      const man = (data.matched || []).filter((m) => m.action === "manual").length;
      const docInfo = data.document_number ? ` · N. doc: ${data.document_number}` : "";
      toast.success(`DDT letto: ${auto} automatici, ${sub} sostituzioni, ${man} manuali${docInfo}`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Errore lettura DDT";
      toast.error(typeof msg === "string" ? msg : "Errore lettura DDT");
    } finally {
      setScanning(false);
    }
  };

  const attachPdf = async () => {
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "DDT.pdf");
      const r = await axios.post(`${API}/orders/${orderId}/ddt-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onAttached?.(r.data);
      toast.success("PDF del DDT allegato all'ordine");
    } catch {
      toast.error("Allegato DDT non salvato — puoi ricaricarlo dal riquadro Rif. documento");
    } finally {
      setAttaching(false);
    }
  };

  const apply = async () => {
    if (!result?.matched) return;
    const headerMeta = {
      document_number: result.document_number || null,
      document_date: result.document_date || null,
    };
    const num = (v) => {
      const n = parseFloat(v);
      return isNaN(n) ? null : n;
    };
    if (replaceMode) {
      const newLines = [];
      (result.matched || []).forEach((m) => {
        const d = decisions[m.ddt_index] || {};
        const qty = parseFloat(d.quantity);
        if (!qty || qty <= 0) return;
        const price = num(d.price);
        const disc = num(d.discount_percent) ?? 0;
        newLines.push({
          order_item_index: d.order_item_index,  // may be null
          code: d.code || m.scanned_code || "",
          description: d.description || m.scanned_description || "",
          quantity: qty,
          unit: d.unit || m.scanned_unit || "Kg",
          price: price != null && price > 0 ? price : null,
          discount_percent: disc,
          action: d.order_item_index == null ? "manual" : (d.action || "substitute"),
        });
      });
      if (newLines.length === 0) {
        toast.error("Inserisci almeno una riga con peso > 0");
        return;
      }
      onApply?.({ mode: "replace", lines: newLines, ...headerMeta });
      await attachPdf();
      onClose?.();
      toast.success(`${newLines.length} righe sostituite all'ordine`);
      return;
    }
    // MATCH/MERGE MODE: patch the matched order items with actual data
    const patches = {};
    (result.matched || []).forEach((m) => {
      const d = decisions[m.ddt_index] || {};
      const oi = d.order_item_index;
      if (oi == null || oi < 0) return; // manual → skip; magazzino lo inserirà a mano
      const qty = parseFloat(d.quantity);
      if (!qty || qty <= 0) return;
      const price = num(d.price);
      const disc = num(d.discount_percent) ?? 0;
      patches[oi] = {
        actual_quantity: qty,
        unit: d.unit || m.scanned_unit || undefined,
        actual_code: d.code || undefined,
        actual_description: d.description || undefined,
        price: price != null && price > 0 ? price : undefined,
        discount_percent: disc,
        action: d.action,
      };
    });
    const count = Object.keys(patches).length;
    if (count === 0) {
      toast.error("Nessuna riga da applicare. Imposta almeno una corrispondenza.");
      return;
    }
    onApply?.({ mode: "merge", patches, ...headerMeta });
    await attachPdf();
    onClose?.();
    toast.success(`${count} righe applicate all'ordine`);
  };

  const updateDecision = (di, patch) => {
    setDecisions((prev) => ({ ...prev, [di]: { ...(prev[di] || {}), ...patch } }));
  };

  const close = () => {
    setFile(null);
    setResult(null);
    setDecisions({});
    onClose?.();
  };

  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl bg-white max-h-[95vh] overflow-y-auto p-0" data-testid="ddt-scanner-dialog">
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle className="flex items-center gap-2 text-stone-900">
            <ScanLine className="w-5 h-5 text-[#8A1C2A]" />
            Leggi DDT da PDF
          </DialogTitle>
          <p className="text-xs text-stone-500">
            Carica il PDF del DDT: codici, pesi, prezzi e sconti vengono estratti automaticamente con AI.
          </p>
        </DialogHeader>

        {/* UPLOAD STAGE */}
        {!result && (
          <div className="p-5 pt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={onPickFile}
              className="hidden"
              data-testid="ddt-file-input"
            />
            {!file ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-stone-300 rounded-xl py-14 flex flex-col items-center gap-3 text-stone-500 hover:border-[#8A1C2A] hover:text-[#8A1C2A] transition-colors"
                data-testid="ddt-upload-btn"
              >
                <Upload className="w-10 h-10" />
                <div className="text-center">
                  <p className="text-sm font-semibold">Carica il PDF del DDT</p>
                  <p className="text-xs mt-1">Solo file PDF · verrà letto con AI e allegato all'ordine</p>
                </div>
              </button>
            ) : (
              <div className="border border-stone-200 rounded-xl p-4 bg-stone-50" data-testid="ddt-pdf-preview">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#8A1C2A]/10 flex items-center justify-center shrink-0">
                    <FileText className="w-6 h-6 text-[#8A1C2A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-stone-900 truncate">{file.name || "DDT.pdf"}</p>
                    <p className="text-xs text-stone-500">{fmtSize(file.size || 0)} · pronto per la lettura AI</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      size="sm"
                      variant="outline"
                      className="border-stone-300"
                      data-testid="ddt-file-replace-btn"
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sostituisci
                    </Button>
                    <Button
                      onClick={removeFile}
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="ddt-file-remove-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Rimuovi
                    </Button>
                  </div>
                </div>
                <div className="flex justify-center mt-4">
                  <Button
                    onClick={sendToServer}
                    disabled={scanning}
                    className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
                    data-testid="ddt-scan-btn"
                  >
                    {scanning ? (
                      <><div className="spinner w-4 h-4 mr-2" /> Lettura AI in corso...</>
                    ) : (
                      <><ScanLine className="w-4 h-4 mr-2" /> Leggi DDT con AI</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* RESULTS STAGE */}
        {result && (
          <div className="p-5 pt-2">
            {(result.document_number || result.document_date) && (
              <div className="mb-2 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-900" data-testid="ddt-doc-number-banner">
                {result.document_number && <span>📄 N. documento rilevato: <strong>{result.document_number}</strong> (verrà inserito nel Rif. DDT)</span>}
                {result.document_date && <span>· Data: <strong>{result.document_date}</strong></span>}
              </div>
            )}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-sm text-stone-700">
                <strong>{(result.matched || []).length}</strong> righe estratte dal DDT. Verifica pesi, prezzi e sconti, poi conferma.
              </p>
              <div className="flex gap-2">
                <Button onClick={removeFile} size="sm" variant="outline" className="border-stone-300" data-testid="ddt-rescan-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Nuovo PDF
                </Button>
              </div>
            </div>

            {/* MODE SELECTOR */}
            <div className={`mb-3 p-3 rounded-lg border ${replaceMode ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-300'}`} data-testid="ddt-mode-panel">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={replaceMode}
                  onChange={(e) => setReplaceMode(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-red-600"
                  data-testid="ddt-replace-mode-toggle"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-stone-900">
                    {replaceMode ? "🔄 Sostituzione completa" : "➕ Aggiungi/Aggiorna righe"}
                  </p>
                  <p className="text-xs text-stone-600 mt-0.5">
                    {replaceMode
                      ? "ATTENZIONE: tutte le righe attuali dell'ordine verranno rimpiazzate con quelle del DDT (pesi + prezzi + sconti effettivi)."
                      : "Aggiorna pesi, prezzi e sconti sulle righe corrispondenti dell'ordine. Le righe non matchate vanno gestite a mano."}
                  </p>
                </div>
              </label>
            </div>

            <div className="border border-stone-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left p-2 text-xs font-semibold text-stone-600">Letto dal DDT (modificabile)</th>
                    <th className="text-left p-2 text-xs font-semibold text-stone-600">Articolo ordine</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600 w-24">Quantità</th>
                    <th className="text-left p-2 text-xs font-semibold text-stone-600 w-20">U.M.</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600 w-24">Prezzo €</th>
                    <th className="text-right p-2 text-xs font-semibold text-stone-600 w-20">Sconto %</th>
                    <th className="text-center p-2 text-xs font-semibold text-stone-600 w-24">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.matched || []).map((m, di) => {
                    const d = decisions[m.ddt_index] || {};
                    const oi = d.order_item_index;
                    return (
                      <tr key={di} className="border-b border-stone-100 align-top" data-testid={`ddt-row-${di}`}>
                        <td className="p-2 space-y-1">
                          <Input
                            value={d.code ?? ""}
                            onChange={(e) => updateDecision(m.ddt_index, { code: e.target.value })}
                            className="h-7 text-xs font-mono bg-white border-stone-300"
                            placeholder="Codice"
                            data-testid={`ddt-code-${di}`}
                          />
                          <Input
                            value={d.description ?? ""}
                            onChange={(e) => updateDecision(m.ddt_index, { description: e.target.value })}
                            className="h-7 text-xs bg-white border-stone-300"
                            placeholder="Descrizione"
                            data-testid={`ddt-desc-${di}`}
                          />
                        </td>
                        <td className="p-2">
                          <Select
                            value={oi == null ? "__none__" : String(oi)}
                            onValueChange={(v) => updateDecision(m.ddt_index, {
                              order_item_index: v === "__none__" ? null : parseInt(v, 10),
                              action: v === "__none__" ? "manual" : (parseInt(v, 10) === m.order_item_index ? m.action : "substitute"),
                            })}
                          >
                            <SelectTrigger className="h-8 text-xs bg-white border-stone-300" data-testid={`ddt-select-${di}`}>
                              <SelectValue placeholder="-- inserimento manuale --" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">-- inserimento manuale --</SelectItem>
                              {orderItems.map((it, idx) => (
                                <SelectItem key={idx} value={String(idx)}>
                                  {it.code || "—"} · {(it.description || "").slice(0, 40)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {oi != null && orderItems[oi] && m.scanned_code && orderItems[oi].code !== m.scanned_code && (
                            <div className="text-[10px] text-amber-700 mt-1 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Codice DDT diverso dall'ordine: <strong>sostituzione</strong>
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            step="0.001"
                            value={d.quantity ?? ""}
                            onChange={(e) => updateDecision(m.ddt_index, { quantity: e.target.value })}
                            className="h-8 text-xs bg-white border-stone-300 text-right"
                            data-testid={`ddt-qty-${di}`}
                          />
                        </td>
                        <td className="p-2">
                          <Select value={d.unit || "Kg"} onValueChange={(v) => updateDecision(m.ddt_index, { unit: v })}>
                            <SelectTrigger className="h-8 text-xs bg-white border-stone-300 w-16 px-2" data-testid={`ddt-unit-${di}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Kg">Kg</SelectItem>
                              <SelectItem value="Pz">Pz</SelectItem>
                              <SelectItem value="CT">CT</SelectItem>
                            </SelectContent>
                          </Select>
                          {oi != null && orderItems[oi]?.unit && (d.unit || "Kg").toLowerCase() !== (orderItems[oi].unit || "Kg").toLowerCase() && (
                            <div className="text-[10px] text-amber-700 mt-1 whitespace-nowrap" data-testid={`ddt-unit-diff-${di}`}>
                              ordine: {orderItems[oi].unit}
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            step="0.01"
                            value={d.price ?? ""}
                            onChange={(e) => updateDecision(m.ddt_index, { price: e.target.value })}
                            className="h-8 text-xs bg-white border-stone-300 text-right"
                            placeholder="—"
                            data-testid={`ddt-price-${di}`}
                          />
                        </td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            max="100"
                            value={d.discount_percent ?? ""}
                            onChange={(e) => updateDecision(m.ddt_index, { discount_percent: e.target.value })}
                            className="h-8 text-xs bg-white border-stone-300 text-right"
                            placeholder="0"
                            data-testid={`ddt-discount-${di}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          {oi == null ? (
                            <Badge className="bg-stone-100 text-stone-600 text-[10px]">manuale</Badge>
                          ) : d.action === "auto" ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]"><Check className="w-3 h-3 inline mr-0.5" />auto</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 text-[10px]">sostituz.</Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="px-5 pb-5 gap-2">
          <Button variant="outline" onClick={close} className="border-stone-300" data-testid="ddt-close-btn">
            <X className="w-4 h-4 mr-2" /> Chiudi
          </Button>
          {result && (
            <Button
              onClick={apply}
              disabled={attaching}
              className={replaceMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-[#8A1C2A] hover:bg-[#6B1420] text-white"}
              data-testid="ddt-apply-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              {attaching ? "Allego PDF..." : replaceMode ? "Sostituisci tutto" : "Applica all'ordine"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DDTCameraScanner;
